import { useEffect, useState } from 'react';
import { toNumber } from 'lodash';
import { toast } from 'react-toastify';
import { t } from 'i18next';

import { IWelcomeLettersTableFilters } from '@views/pages/DataAnalysis/components/WelcomeLetters/hooks/useWelcomeLetters.ts';
import { WelcomeLettersColumnNames } from '@views/pages/DataAnalysis/components/WelcomeLetters/components';
import { useWelcomeLettersDownloadQuery } from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';

interface IUseWelcomeLettersTableProps {
  filters: IWelcomeLettersTableFilters;
  setFilters: (filters: IWelcomeLettersTableFilters) => void;
}

export const useWelcomeLettersTable = ({
  filters,
  setFilters,
}: IUseWelcomeLettersTableProps) => {
  const { sort_by, sort_order } = filters;
  const [downloadId, setDownloadId] = useState<string>('');

  const { data, isPending, mutateAsync } =
    useWelcomeLettersDownloadQuery(downloadId);

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);

      const a = document.createElement('a');
      a.href = url;
      a.download = `submission-${downloadId}`;

      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
          setDownloadId('');
        }, 150);
      }

      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  const handleDownload = async (welcome_id: string) => {
    toast.info(t('welcomeLetters.toast.loading'));
    setDownloadId(welcome_id);

    try {
      await mutateAsync();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleChangeOrder = (colName: WelcomeLettersColumnNames) => {
    setFilters({
      ...filters,
      sort_by: colName,
      sort_order: filters.sort_order === 'asc' ? 'desc' : 'asc',
    });
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setFilters({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, limit: toNumber(event.target.value), page: 1 });
  };

  return {
    sort_by,
    sort_order,
    onPageIndexChange,
    onPageSizeChange,
    handleChangeOrder,
    handleDownload,
    isDownloading: isPending,
  };
};
